import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { LoginRequestDto } from '../../../../core/api/model/loginRequestDto';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LegalLinksContainerComponent } from '../../../../shared/components/legal-links-container/legal-links-container.component';
import { FormElementComponent } from '../../../../shared/components/form-inputs/components/form-element/form-element.component';
import { TogglePasswordDirective } from '../../../../shared/directives/toggle-password.directive';
import { customEmailValidator } from '../../../../shared/validators/customEmailValidator';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { FeedbackService } from '../../../../services/feedback.service';
import { ApiUtils } from '../../../../core/helper/api.utils';
import { AuthService } from '../../../../services/auth.service';
import { ConfigService } from '../../../../services/config.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoDirective,
    LegalLinksContainerComponent,
    FormElementComponent,
    TogglePasswordDirective,
    LoaderComponent
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  standalone: true
})
export class LoginComponent {
  private readonly authService = inject(AuthService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  public loginForm: FormGroup;
  public isLoading = false;

  /** URL for the registration link: configured landing (e.g. pricing) or login if not set. */
  public get registrationLandingUrl(): string {
    return ConfigService.getRegistrationLandingPageUrl();
  }

  public onRegistrationClick(event: Event): void {
    const url = this.registrationLandingUrl;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return;
    }
    event.preventDefault();
    this.router.navigateByUrl(url);
  }

  constructor() {
    this.loginForm = new FormGroup({
      email: new FormControl('', {
        validators: [Validators.required, Validators.email, customEmailValidator()],
        updateOn: 'blur'
      }),
      password: new FormControl('', [Validators.required])
    });
  }

  /**
   * Performs the login, navigates to the main module after login or handles errors.
   */
  public async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;

    const loginData: LoginRequestDto = this.loginForm.value;

    try {
      await this.authService.login(loginData);
      this.router.navigate(['/main/dashboard']);
    } catch (e) {
      const error = ApiUtils.createAppErrorFromResponse(e);

      if (error.errorCode === 'NotFound') {
        this.loginForm.controls['email'].setErrors({ userNotFound: true });
        this.loginForm.controls['email'].markAsTouched();
      } else if (error.errorCode === 'InvalidCredentials') {
        this.loginForm.controls['password'].setErrors({ wrongPassword: true });
        this.loginForm.controls['password'].markAsTouched();
      } else {
        this.feedbackService.error(this.translocoService.translate('errors.generalError'));
      }
    } finally {
      this.isLoading = false;
    }
  }
}
